import { type ReactNode } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { HomePage } from '@/modules/home';
import { GlowOrb } from '@/components/effects/glow-orb';
import {
  useAuth,
  useAccountStatus,
  useRequireAuth,
  useRequireAdmin,
  useRequireAdminOrCollector,
  useRequireNotVerifying,
  useRequireNotGameRulesGated,
} from '@/hooks/use-auth';
import { useAccessGuard } from '@/hooks/use-access-guard';
import { useForcePasswordChange } from '@/hooks/use-force-password-change';

function GuardLoader(): ReactNode {
  return (
    <div className="relative flex min-h-dvh items-center justify-center bg-void">
      <GlowOrb />
    </div>
  );
}

export function PublicHome(): ReactNode {
  const { isAuthenticated } = useAuth();
  const status = useAccountStatus();
  if (!isAuthenticated) return <HomePage />;
  if (status === 'verifying') return <Navigate to="/team/collectors" replace />;
  return <Navigate to="/creators" replace />;
}

export function ProtectedRoute(): ReactNode {
  useRequireAuth();
  useRequireNotVerifying();
  useRequireNotGameRulesGated();
  useForcePasswordChange();
  const { checking } = useAccessGuard();
  if (checking) return <GuardLoader />;
  return <Outlet />;
}

export function AuthOnlyRoute(): ReactNode {
  useRequireAuth();
  useRequireNotGameRulesGated();
  useForcePasswordChange();
  const { checking } = useAccessGuard();
  if (checking) return <GuardLoader />;
  return <Outlet />;
}

export function AdminRoute(): ReactNode {
  useRequireAdmin();
  return <Outlet />;
}

export function AdminOrCollectorRoute(): ReactNode {
  useRequireAdminOrCollector();
  return <Outlet />;
}

export function NavigateCollector(): ReactNode {
  const { collectorId } = useParams();
  return <Navigate to={`/progress/${collectorId}`} replace />;
}
